import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@resource-kit/ui/react';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useToast } from '@components/ui/Toast';
import { usePermissions } from '@hooks/useAuth';
import { http } from '@lib/api';
import { resolveApiError } from '@lib/errors';
import { queryKeys } from '@lib/queryKeys';
import { EXTERNAL_URLS } from '@/constants/config';
import { PERMISSIONS } from '@/permissions';
import type { components } from '@/types/api.generated';
import { useAssignmentProgress } from '../hooks/useAssignmentProgress';
import { useDeliveryFlow } from '../hooks/useDeliveryFlow';

type ImportOnfleetDriversResult = components['schemas']['ImportOnfleetDriversResult'];

interface AssignmentPaneProps {
  date: string;
  /** Selects the same day's awaiting-departure cell. */
  onGoReady: () => void;
}

/**
 * Detail pane of the awaiting-assignment column (#2439, #2471). The numbers
 * come from `GET /api/dispatch/assignment`. Units differ by row: the progress
 * line counts driver tasks, the two rows count stops (delivery jobs), so they
 * are never added up. The driver import reads the Onfleet roster and writes
 * only the local driver list (plus the driver on delivery jobs that were
 * waiting for that driver), so its mutation lives here rather than on the page.
 */
export function AssignmentPane({ date, onGoReady }: AssignmentPaneProps) {
  const { t, i18n } = useTranslation();
  const { confirm } = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();
  const progress = useAssignmentProgress(date);
  const flow = useDeliveryFlow();
  const pickupTime = flow.data?.gates.find((g) => g.kind === 'dispatch_cutoff')?.local_time;
  const canImport = usePermissions().includes(PERMISSIONS.DISPATCH_WRITE);

  const importDrivers = useMutation<ImportOnfleetDriversResult, unknown, void>({
    mutationFn: () =>
      http.post<ImportOnfleetDriversResult>('/api/drivers/import-onfleet', { dryRun: false }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all() });
      // The backfill moves jobs out of the awaiting-assignment column.
      queryClient.invalidateQueries({ queryKey: queryKeys.dispatch.all() });
      toast.info(
        t('dispatch.pane.assignment.import'),
        t('dispatch.pane.assignment.importDone', {
          created: result.created,
          updated: result.updated,
          invalid: result.invalid,
          backfilled: result.backfilled,
        }),
      );
    },
    onError: (err) => {
      toast.error(
        t('dispatch.pane.assignment.importFailed'),
        resolveApiError(err, i18n, { namespace: 'dispatch', fallbackKey: 'error.unknown' }),
      );
    },
  });

  const handleImport = async () => {
    const yes = await confirm({
      title: t('dispatch.pane.assignment.importConfirmTitle'),
      message: t('dispatch.pane.assignment.importConfirm'),
    });
    if (yes) importDrivers.mutate();
  };

  const data = progress.data;
  const withDriver = data?.driver_tasks.with_driver ?? 0;
  const total = data?.driver_tasks.total ?? 0;
  const percent = total > 0 ? Math.round((100 * withDriver) / total) : 0;
  const tone = (n: number) => (n > 0 ? 'text-warning' : 'text-muted-foreground');
  const unassignedJobs = data?.unassigned.jobs ?? 0;
  const unmappedJobs = data?.unmapped_worker.jobs ?? 0;
  const stops = (n: number | undefined) =>
    n === undefined ? '—' : t('dispatch.pane.assignment.stops', { count: n });

  return (
    <div
      data-testid="dispatch-pane-assignment"
      // The design collapses to one column at 980px and below.
      className="grid items-start gap-6 min-[981px]:grid-cols-[minmax(0,1fr)_340px]"
    >
      <section className="flex flex-col gap-2.5">
        <div className="flex items-baseline gap-2">
          <h3 className="text-[14.5px] font-semibold">
            {t('dispatch.pane.assignment.progressTitle')}
          </h3>
          <span className="text-xs text-muted-foreground">
            {t('dispatch.pane.assignment.progressHint')}
          </span>
        </div>
        <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
          <div className="flex flex-col gap-2 px-4 py-3.5">
            {progress.isError ? (
              <p className="text-xs text-muted-foreground">
                {t('dispatch.pane.assignment.loadFailed')}
              </p>
            ) : (
              <div className="flex items-baseline gap-2">
                <span
                  data-testid="dispatch-assign-with-driver"
                  className="text-[22px] font-display font-bold tabular-nums"
                >
                  {data ? withDriver : '—'}
                </span>
                {data ? (
                  <span className="text-xs text-muted-foreground">
                    {t('dispatch.pane.assignment.progressCount', { count: total })}
                  </span>
                ) : null}
                <span className="flex-1" />
                <a
                  data-testid="dispatch-open-onfleet"
                  href={EXTERNAL_URLS.onfleetDashboard}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 items-center rounded-md border border-border px-3 text-xs font-medium hover:bg-muted"
                >
                  {t('dispatch.pane.assignment.openOnfleet')}
                </a>
              </div>
            )}
            <div
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-2 overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full bg-primary" style={{ width: `${percent}%` }} />
            </div>
          </div>
          <div
            data-testid="dispatch-assign-unassigned"
            className="grid grid-cols-[10px_minmax(0,1fr)_64px_80px_auto] items-center gap-3 border-t px-4 py-2.5 text-[13px]"
          >
            <span className="size-2.5 rounded-micro bg-muted" />
            <span className={tone(unassignedJobs)}>{t('dispatch.pane.assignment.unassigned')}</span>
            <span className={`text-right font-mono ${tone(unassignedJobs)}`}>
              {stops(data?.unassigned.jobs)}
            </span>
            <span className="text-xs text-muted-foreground">
              {pickupTime ? t('dispatch.pane.assignment.beforePickup', { time: pickupTime }) : null}
            </span>
            <span />
          </div>
          <div
            data-testid="dispatch-assign-unmapped"
            className="grid grid-cols-[10px_minmax(0,1fr)_64px_80px_auto] items-center gap-3 border-t px-4 py-2.5 text-[13px]"
          >
            <span className="size-2.5 rounded-micro bg-muted" />
            <span className={tone(unmappedJobs)}>{t('dispatch.pane.assignment.unmapped')}</span>
            <span className={`text-right font-mono ${tone(unmappedJobs)}`}>
              {stops(data?.unmapped_worker.jobs)}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('dispatch.pane.assignment.newDriverNotImported')}
            </span>
            {canImport ? (
              <Button
                data-testid="dispatch-import-drivers"
                size="sm"
                variant="outline"
                onClick={() => void handleImport()}
                disabled={importDrivers.isPending}
              >
                {t('dispatch.pane.assignment.import')}
              </Button>
            ) : (
              <span />
            )}
          </div>
        </div>
      </section>
      <aside className="flex flex-col gap-3 rounded-xl bg-card p-4 ring-1 ring-border">
        <span className="text-xs text-muted-foreground">
          {t('dispatch.pane.assignment.printTitle')}
        </span>
        <p className="text-[13px]">{t('dispatch.pane.assignment.printBody')}</p>
        <Button
          data-testid="dispatch-go-ready"
          variant="outline"
          className="h-11 justify-center text-[14.5px]"
          onClick={onGoReady}
        >
          {t('dispatch.pane.assignment.goReady')}
        </Button>
      </aside>
    </div>
  );
}
